import { Note } from '@fullstory/client-api';
import { SessionModel } from '@fullstory/playback-model';
import { combineReducers } from '@fullstory/redux-util/utils';
import { cookedEventText, flattenEvents } from './utils';
import { PlaybackActions } from './actions';
import { FakeCookedEvent, PlaybackTypes } from './types';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';

interface EventListItem {
  Key: string;
}

export interface EventListEvent
  extends FakeCookedEvent,
    EventListItem,
    ReturnType<typeof cookedEventText> {
  LinearizedTime: number;
  AbsTime: number;
  UserId: string;
  SessionId: string;
  PageId: string;
  NativePlatform: NativePlatform;
}

export interface EventListNote extends Note, EventListItem {
  LinearizedTime: number;
}

export interface SessionState {
  session: SessionModel | null; // Mutable session model. We need this to do things like update from Go Live trailing data. Not used to detect redux state changes.
  events: EventListEvent[]; // List of events in the session. Changes to this will be computed from session, but we will treat it as normal immutable redux data.
}

const getNote = (n: Note): EventListNote => ({
  ...n,
  Key: n.Id,
  LinearizedTime: n.AbsTime,
});

const getNotes = (session: SessionModel): EventListNote[] =>
  [...session.linearized.pages]
    .map(page => [...page.notes].map(getNote))
    .reduce((allNotes, notes) => [...allNotes, ...notes], []);

const sessionState = (
  state: SessionState | null = null,
  action: PlaybackActions,
): SessionState | null => {
  switch (action.type) {
    case PlaybackTypes.ClearSession: {
      return { session: null, events: [] };
    }
    case PlaybackTypes.LoadSession: {
      const { flags, session } = action;
      const events = flattenEvents(session, flags);
      return { session, events };
    }
    case PlaybackTypes.UpdateWithLiveData: {
      const { flags } = action;
      if (state == null) {
        return state;
      }
      const { session } = state;
      const events = session ? flattenEvents(session, flags) : [];
      return { session, events };
    }
  }
  return state;
};

const noteReducer = (state: Note, action: PlaybackActions): Note => {
  switch (action.type) {
    case PlaybackTypes.CreateShareLinkSuccess: {
      if (state.Id !== action.data.noteId) {
        return state;
      }

      return {
        ...state,
        ShareLink: action.data.shareLink,
      };
    }
    case PlaybackTypes.AddSentNote: {
      if (state.Id !== action.note.Id) {
        return state;
      }

      return {
        ...state,
        SentNotes: {
          ...(state.SentNotes || {}),
          [action.integrationId]: action.url,
        },
      };
    }
  }

  return state;
};

const notes = (state: EventListNote[] = [], action: PlaybackActions): EventListNote[] => {
  switch (action.type) {
    case PlaybackTypes.LoadSession: {
      const { session } = action;
      return getNotes(session);
    }
    case PlaybackTypes.AddNote: {
      const { note } = action;
      return [...state, getNote(note)];
    }
    case PlaybackTypes.DeleteNoteSuccess:
      return state.filter(
        n =>
          n.Id !== action.data.Id ||
          n.SessionId !== action.data.SessionId ||
          n.PageId !== action.data.PageId,
      );
    case PlaybackTypes.CreateShareLinkSuccess:
      return state.map(n => getNote(noteReducer(n, action)));
    case PlaybackTypes.AddSentNote:
      return state.map(n => getNote(noteReducer(n, action)));
  }
  return state;
};

const time = (state: number = 0, action: PlaybackActions) => {
  switch (action.type) {
    case PlaybackTypes.TimeChange:
      return action.time;
  }
  return state;
};

const sexpr = (state = '', action: PlaybackActions) => {
  switch (action.type) {
    case PlaybackTypes.LoadSession:
      return action.sexpr;
  }
  return state;
};

const types = (state: string[] = [], action: PlaybackActions) => {
  switch (action.type) {
    case PlaybackTypes.ClearEventTypesFilter:
      return [];
    case PlaybackTypes.ClearAllFilters:
      return [];
    case PlaybackTypes.ToggleEventTypesFilter: {
      const { eventType } = action;
      if (state.includes(eventType)) {
        return state.filter(t => t !== eventType);
      }
      return [...state, eventType];
    }
  }
  return state;
};

const text = (state = '', action: PlaybackActions) => {
  switch (action.type) {
    case PlaybackTypes.SetTextFilter:
      return action.filter;
    case PlaybackTypes.ClearAllFilters:
      return '';
  }
  return state;
};

type EventFiltersState = {
  types: ReturnType<typeof types>;
  text: ReturnType<typeof text>;
};

const eventFilters = combineReducers<EventFiltersState>({
  types,
  text,
});

type PlaybackReducerState = {
  time: ReturnType<typeof time>;
  sessionState: ReturnType<typeof sessionState>;
  sexpr: ReturnType<typeof sexpr>;
  notes: ReturnType<typeof notes>;
  eventFilters: ReturnType<typeof eventFilters>;
};

export const playbackReducer = combineReducers<PlaybackReducerState>({
  time,
  sessionState,
  sexpr,
  notes,
  eventFilters,
});
